import { useCallback, useEffect, useRef } from 'react';
import { QueryClient } from '@tanstack/react-query';
import { IMessage } from '../models/Message';
import { addMessageToCache, replacePendingMessage, updateMessageReactionInCache } from './useConversationMessages';
import { CanvasCommand, PubSubCommand } from '../constants/pubsubCommands';

export interface PersistentGroupEntry {
  groupId: string;   // instance ID — used as cache key and in group name
  summaryId: string; // parent collab ID — used to match WS message collaborationId
}

interface UsePersistentChatGroupsOptions {
  socket: WebSocket | undefined | null;
  tenantId: string;
  queryClient: QueryClient;
  uId: string;
  activeGroupId: string | undefined;
  /** Called when a new message arrives for a group that is not currently active. */
  onBackgroundMessage?: (groupId: string, summaryId: string) => void;
  /** Called when the server sends a canvas control command (show/hide/load). */
  onCanvasCommand?: (cmd: CanvasCommand) => void;
}

interface UsePersistentChatGroupsResult {
  subscribeToGroup: (entry: PersistentGroupEntry) => void;
}

/**
 * Manages WebSocket group subscriptions that persist across conversation switches.
 * Once a group is joined it is never left during the session, so messages arriving
 * for background conversations are silently written to the React Query cache.
 *
 * Must be instantiated at a level that survives conversation switches
 * (e.g. Workspaces) — NOT inside ChatPanel, which remounts on every switch.
 */
export function usePersistentChatGroups({
  socket,
  tenantId,
  queryClient,
  uId,
  activeGroupId,
  onBackgroundMessage,
  onCanvasCommand,
}: UsePersistentChatGroupsOptions): UsePersistentChatGroupsResult {
  // Persistent map of all groups we have subscribed to this session.
  // key = groupId, value = { groupId, summaryId }
  const joinedGroupsRef = useRef<Map<string, PersistentGroupEntry>>(new Map());

  // Keep a ref to activeGroupId so the message handler never goes stale
  // without needing to be re-registered on every conversation switch.
  const activeGroupIdRef = useRef(activeGroupId);
  useEffect(() => {
    activeGroupIdRef.current = activeGroupId;
  }, [activeGroupId]);

  const onBackgroundMessageRef = useRef(onBackgroundMessage);
  useEffect(() => {
    onBackgroundMessageRef.current = onBackgroundMessage;
  }, [onBackgroundMessage]);

  const onCanvasCommandRef = useRef(onCanvasCommand);
  useEffect(() => {
    onCanvasCommandRef.current = onCanvasCommand;
  }, [onCanvasCommand]);

  /**
   * Subscribe to a group. Idempotent — sends joinGroup only once per session.
   */
  const subscribeToGroup = useCallback(
    (entry: PersistentGroupEntry) => {
      if (!tenantId || !entry.groupId) return;
      if (joinedGroupsRef.current.has(entry.groupId)) return;

      joinedGroupsRef.current.set(entry.groupId, entry);

      if (socket && socket.readyState === WebSocket.OPEN) {
        socket.send(JSON.stringify({ type: 'joinGroup', group: `${tenantId}_${entry.groupId}` }));
      }
      // If socket is not open yet, the reconnect effect below will join when it opens.
    },
    [socket, tenantId],
  );

  // When the socket (re)connects, rejoin every group we know about.
  useEffect(() => {
    if (!socket) return;

    const handleOpen = () => {
      joinedGroupsRef.current.forEach(({ groupId }) => {
        socket.send(JSON.stringify({ type: 'joinGroup', group: `${tenantId}_${groupId}` }));
      });
    };

    // If already open at effect time (e.g. on hot-reload), join immediately.
    if (socket.readyState === WebSocket.OPEN) {
      handleOpen();
    }

    socket.addEventListener('open', handleOpen);
    return () => socket.removeEventListener('open', handleOpen);
  }, [socket, tenantId]);

  // Single global message handler — routes messages to the correct cache
  // for ALL subscribed groups, regardless of which is currently active.
  useEffect(() => {
    if (!socket) return;

    const handleMessage = (event: MessageEvent) => {
      try {
        const msg = JSON.parse(event.data);
        if (msg.type !== 'message' || msg.from !== 'group' || msg.dataType !== 'json') return;

        const inner = JSON.parse(msg.data);

        if (inner.type === PubSubCommand.UserTyping) return;

        // Prefer routing via the group name from the WS envelope — it always
        // contains the exact instance ID regardless of what collaborationId the
        // backend embeds in the payload (which may be the parent collab ID).
        let matchedEntry: PersistentGroupEntry | undefined;

        if (msg.group) {
          let instanceId: string | null = null;
          if (tenantId && msg.group.startsWith(`${tenantId}_`)) {
            instanceId = msg.group.slice(tenantId.length + 1);
          } else if (msg.group.startsWith('collab_')) {
            instanceId = msg.group.slice('collab_'.length);
          }
          if (instanceId) {
            matchedEntry = joinedGroupsRef.current.get(instanceId);
          }
        }

        // Fallback: match by collaborationId in the payload
        if (!matchedEntry) {
          matchedEntry = Array.from(joinedGroupsRef.current.values()).find(
            (entry) =>
              inner.collaborationId === entry.groupId ||
              inner.collaborationId === entry.summaryId,
          );
        }

        if (!matchedEntry) return;

        if (inner.eventType === 'assistant_internal_message') return;

        const { groupId, summaryId } = matchedEntry;
        const isBackground = groupId !== activeGroupIdRef.current;

        // Canvas panel control commands — handled before chat-message routing
        if (
          inner.type === PubSubCommand.CanvasShow ||
          inner.type === PubSubCommand.CanvasHide ||
          inner.type === PubSubCommand.CanvasLoadDocument
        ) {
          onCanvasCommandRef.current?.(inner as CanvasCommand);
          return;
        }

        if (inner.type === 'system' && inner.message) {
          const sysMsg: IMessage = {
            id: inner.id,
            pk: inner.id,
            collaborationId: inner.collaborationId,
            tenantId,
            createdDateTime: inner.timestamp ?? new Date().toISOString(),
            lastEditedDateTime: null,
            sender: {
              id: inner.authorUid || 'system',
              role: 'system',
              displayName: 'SYSTEM',
              avatarUrl: '',
            },
            contentBlocks: [{ type: 'text', content: inner.message }],
            plainTextContent: inner.message,
            replyToId: null,
            status: 'active',
            interactions: { reactions: {}, aiQuality: [] },
            clientMetadata: {
              userAgent: 'WebSocket',
              clientVersion: 'N/A',
              timeZone: 'UTC',
              deviceId: 'websocket',
              ipAddress: 'N/A',
            },
            editHistory: [],
          };
          addMessageToCache(queryClient, groupId, sysMsg);
        } else if (inner.type === 'message_reaction') {
          updateMessageReactionInCache(
            queryClient,
            groupId,
            inner.messageId,
            inner.interactions?.reactions || {},
          );
        } else if (inner.status === 'streaming' && inner.session_id) {
          const skeletal: IMessage = {
            id: inner.messageId ?? inner.id,
            pk: inner.messageId ?? inner.id,
            collaborationId: groupId,
            tenantId,
            createdDateTime: inner.timestamp ?? new Date().toISOString(),
            lastEditedDateTime: null,
            sender: inner.sender,
            contentBlocks: [],
            plainTextContent: '',
            replyToId: inner.replyToId ?? null,
            status: 'streaming',
            session_id: inner.session_id,
            isStreaming: true,
            interactions: { reactions: {}, aiQuality: [] },
            clientMetadata: { userAgent: '', clientVersion: '', timeZone: '', deviceId: '', ipAddress: '' },
            editHistory: [],
          };
          addMessageToCache(queryClient, groupId, skeletal);
          if (isBackground) {
            onBackgroundMessageRef.current?.(groupId, summaryId);
          }
        } else if (inner.sender) {
          if (inner.sender.id?.toLowerCase() === uId?.toLowerCase()) {
            replacePendingMessage(queryClient, groupId, inner);
          } else {
            addMessageToCache(queryClient, groupId, inner);
            if (isBackground) {
              onBackgroundMessageRef.current?.(groupId, summaryId);
            }
          }
        }
      } catch (err) {
        console.error('[usePersistentChatGroups] WebSocket parse error', err);
      }
    };

    socket.addEventListener('message', handleMessage);
    return () => socket.removeEventListener('message', handleMessage);
    // uId and tenantId are stable for the session; queryClient is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket]);

  return { subscribeToGroup };
}
